import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import AddressGroupV2 from 'components/field/AddressGroupV2';
import AutoComplete from 'components/field/AutoComplete';
import PhoneNumber from 'components/field/PhoneNumber';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import FormItem from 'components/form/FormItem';
import Typography from 'components/global/Typography';

import useLocale from 'hooks/useLocale';

import { AUTOCOMPLETE } from 'services/consts';

import * as S from '../QuickShipment.styled';

const defaultColSpan = {
	md: 6
};

const warehouseAutocompleteLabels = ['Name'];
const warehouseAutocompleteColumns = [
	{ accessor: 'Name', width: 200 },
	{ accessor: 'Address', width: 350 }
];

const autoFillCustomerDetailsLabels = ['fullName'];
const autoFillCustomerDetailsColumns = [
	{ accessor: 'firstName', width: 150 },
	{ accessor: 'lastName', width: 150 },
	{ accessor: 'phone', width: 150 }
];

const valueOptions = { shouldValidate: true, shouldDirty: true };

const ReceiverDetails = ({ formMode, pickupLocationVisible }) => {
	const { Country } = useLocale();
	const warehousePrefilled = useRef(false);

	const formMethods = useFormContext();

	const {
		setValue,
		watch,
		formState: { isLoading: isFormLoading, isSubmitting, errors }
	} = formMethods;

	const isDifferentAddress = watch('isDifferentAddress');

	const isLoading = isFormLoading || isSubmitting;

	const setMultipleValues = useCallback(
		(values) => {
			Object.entries(values).forEach(([key, value]) => {
				setValue(key, value, { shouldValidate: true, shouldDirty: true });
			});
		},
		[setValue]
	);

	const { addMode, editMode } = useMemo(
		() => ({
			addMode: formMode === 'add',
			editMode: formMode === 'edit'
		}),
		[formMode]
	);

	const handleDifferentAddressToggle = (e) => {
		e.stopPropagation();
		e.preventDefault();
		setValue('isDifferentAddress', !isDifferentAddress, valueOptions);
	};

	useEffect(() => {
		return () => {
			if (!pickupLocationVisible && watch('isDifferentAddress')) {
				setValue('originAddress', watch('newSenderAddress'));
			}
		};
	}, []);

	if (pickupLocationVisible) {
		return (
			<S.CreateShipmentContent>
				<S.Title variant="p2" fontWeight="700">
					receiverContactDetails
				</S.Title>
				<S.CreateShipmentContentWrapper>
					<FormItem
						colSpan={defaultColSpan}
						label="quickShipment.receiverFullName"
						isLoading={isLoading}
						required>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.AUTOFILL_CUSTOMER_DETAILS}
							name="fullName"
							value={watch('autofillID')}
							customValue={watch('fullName')}
							setValue={(value) => {
								if (value === null) {
									setMultipleValues({
										autofillID: null,
										fullName: null
									});
									return;
								}
								if (watch('autofillID') !== value?.ID) {
									setMultipleValues({
										autofillID: value?.ID ?? null,
										fullName: value?.firstName ? `${value.firstName} ${value.lastName}` : null,
										phone: value?.phone ?? null,
										email: value?.email ?? null,
										'address.text': value?.text ?? null,
										'address.shortAddressCode': value?.shortAddressCode ?? null,
										'address.countryName': value?.countryName ?? Country,
										'address.cityName': value?.cityName ?? null,
										'address.stateName': value?.stateName ?? null,
										'address.districtName': value?.districtName ?? null,
										'address.buildingName': value?.buildingName ?? null,
										'address.addressInputType': value?.addressInputType ?? 'fullTextAddress',
										'address.street': value?.street ?? null,
										'address.lat': value?.lat ?? null,
										'address.lng': value?.lng ?? null,
										'address.zipCode': value?.zipCode ?? null
									});
								}
							}}
							textValue={watch('fullName')}
							setTextValue={(value) => {
								setMultipleValues({
									autofillID: null,
									fullName: value
								});
							}}
							columns={autoFillCustomerDetailsColumns}
							selectionLabelAccessor={autoFillCustomerDetailsLabels}
							fixedContainerWidth={false}
						/>
					</FormItem>

					<FormItem
						colSpan={defaultColSpan}
						label="quickShipment.receiverPhoneNumber"
						isLoading={isLoading}
						required>
						<PhoneNumber
							name="phone"
							value={watch('phone')}
							onChange={(e) => {
								setValue('phone', e, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>
				</S.CreateShipmentContentWrapper>
				<S.Line />
				<S.Title variant="p2" fontWeight="700">
					receiverAddressDetails
				</S.Title>
				<AddressGroupV2
					formFieldName="address"
					isLoading={isLoading}
					formMode={formMode}
					autoFillID={watch('autofillID')}
				/>
			</S.CreateShipmentContent>
		);
	}

	return (
		<S.CreateShipmentContent>
			<S.Title variant="p2" fontWeight="700">
				sendFrom
			</S.Title>
			{!isDifferentAddress && (
				<FormItem
					className="form-item-warehouse"
					colSpan={defaultColSpan}
					label="senderLocation"
					required
					isLoading={isLoading}>
					<AutoComplete
						errorMessage={errors?.warehouseID?.message}
						disabled={editMode}
						autoCompleteName={AUTOCOMPLETE.WAREHOUSES}
						setValue={(value) => {
							if (!value) {
								setValue('warehouseID', null, valueOptions);
								setValue('originCountry', null, valueOptions);
								setValue(
									'originAddress',
									{ cityName: null, countryName: null, text: null },
									valueOptions
								);
								return;
							}
							if (addMode) {
								setValue('warehouseID', value.ID, {
									shouldValidate: true,
									shouldDirty: warehousePrefilled.current
								});
								setValue('originCountry', value.Country, {
									shouldValidate: true,
									shouldDirty: warehousePrefilled.current
								});
								setValue(
									'originAddress',
									{
										cityName: value.City,
										countryName: value.Country,
										text: value.Address
									},
									{
										shouldValidate: true,
										shouldDirty: warehousePrefilled.current
									}
								);
								warehousePrefilled.current = true;
							}
						}}
						value={watch('warehouseID')}
						columns={warehouseAutocompleteColumns}
						selectionLabelAccessor={warehouseAutocompleteLabels}
					/>
				</FormItem>
			)}
			<FormItem>
				<S.SwitchInputContainer onClick={handleDifferentAddressToggle}>
					<SwitchInput name="isDifferentAddress" />
					<Typography variant="p3" fontWeight="500">
						sendFromDifferentAddress
					</Typography>
				</S.SwitchInputContainer>
			</FormItem>

			{isDifferentAddress && <AddDifferentAddress />}

			<S.Line />
			<S.Title variant="p2" fontWeight="700">
				sendTo
			</S.Title>
			<S.CreateShipmentContentWrapper>
				<FormItem
					colSpan={defaultColSpan}
					label="quickShipment.receiverFullName"
					isLoading={isLoading}
					required>
					<AutoComplete
						autoCompleteName={AUTOCOMPLETE.AUTOFILL_CUSTOMER_DETAILS}
						name="fullName"
						value={watch('autofillID')}
						customValue={watch('fullName')}
						setValue={(value) => {
							if (value === null) {
								setMultipleValues({
									autofillID: null,
									fullName: null
								});
								return;
							}
							if (watch('autofillID') !== value?.ID) {
								setMultipleValues({
									autofillID: value?.ID ?? null,
									fullName: value?.firstName ? `${value.firstName} ${value.lastName}` : null,
									phone: value?.phone ?? null,
									email: value?.email ?? null,
									'address.text': value?.text ?? null,
									'address.shortAddressCode': value?.shortAddressCode ?? null,
									'address.countryName': value?.countryName ?? Country,
									'address.cityName': value?.cityName ?? null,
									'address.stateName': value?.stateName ?? null,
									'address.districtName': value?.districtName ?? null,
									'address.buildingName': value?.buildingName ?? null,
									'address.addressInputType': value?.addressInputType ?? 'fullTextAddress',
									'address.street': value?.street ?? null,
									'address.lat': value?.lat ?? null,
									'address.lng': value?.lng ?? null,
									'address.zipCode': value?.zipCode ?? null
								});
							}
						}}
						textValue={watch('fullName')}
						setTextValue={(value) => {
							setMultipleValues({
								autofillID: null,
								fullName: value
							});
						}}
						columns={autoFillCustomerDetailsColumns}
						selectionLabelAccessor={autoFillCustomerDetailsLabels}
						fixedContainerWidth={false}
					/>
				</FormItem>

				<FormItem
					colSpan={defaultColSpan}
					label="quickShipment.receiverPhoneNumber"
					isLoading={isLoading}
					required>
					<PhoneNumber
						name="phone"
						value={watch('phone')}
						onChange={(e) => {
							setValue('phone', e, { shouldValidate: true, shouldDirty: true });
						}}
					/>
				</FormItem>
			</S.CreateShipmentContentWrapper>

			<AddressGroupV2
				formFieldName="address"
				isLoading={isLoading}
				formMode={formMode}
				autoFillID={watch('autofillID')}
			/>
		</S.CreateShipmentContent>
	);
};

const AddDifferentAddress = () => {
	const { Country } = useLocale();
	const user = useSelector((state) => state.user);
	const { planName = null, localization: { allowedCountries: allowedCountriesStr } = {} } = user;

	const allowedCountries = useMemo(
		() => (allowedCountriesStr ? [Country, ...allowedCountriesStr.split(',')] : [Country]),
		[allowedCountriesStr]
	);

	const formMethods = useFormContext();

	const {
		setValue,
		watch,
		formState: { isSubmitting }
	} = formMethods;

	return (
		<>
			<S.CreateShipmentContentWrapper className="pt-3">
				<FormItem colSpan={defaultColSpan} label="fullName" isLoading={isSubmitting} required>
					<StringInput name="newSenderFullName" />
				</FormItem>

				<FormItem colSpan={defaultColSpan} label="mobileNumber" isLoading={isSubmitting} required>
					<PhoneNumber
						name="newSenderPhone"
						value={watch('newSenderPhone')}
						onChange={(value) =>
							setValue('newSenderPhone', value, { shouldValidate: true, shouldDirty: true })
						}
					/>
				</FormItem>
				<AddressGroupV2
					formFieldName="newSenderAddress"
					isLoading={isSubmitting}
					restrictions={
						planName !== 'EnterprisePackage'
							? {
									country: {
										isNotDifferent: planName === 'FreePackage',
										code: planName === 'FreePackage' ? Country : allowedCountries
									}
								}
							: null
					}
				/>
			</S.CreateShipmentContentWrapper>
		</>
	);
};

export default ReceiverDetails;
